import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { MIN_COLUMN_WIDTH } from '@document-explorer/core';

/**
 * The drag handle on a column's trailing edge.
 *
 * Deliberately also operable from the keyboard: a resizer that only responds to
 * a pointer drag is unusable for anyone navigating by keyboard, and `separator`
 * is a focusable, arrow-driven role precisely for this. Mirrors the React
 * `ColumnResizer` gesture for gesture.
 */
@Component({
  selector: 'de-column-resizer',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: '',
  host: {
    class: 'de-col-resizer',
    role: 'separator',
    'aria-orientation': 'vertical',
    tabindex: '0',
    '[attr.aria-label]': '"Resize " + label() + " column"',
    '[attr.aria-valuemin]': 'minWidth() ?? MIN',
    '[attr.aria-valuemax]': 'maxWidth() ?? null',
    '(pointerdown)': 'onPointerDown($event)',
    '(pointermove)': 'onPointerMove($event)',
    '(pointerup)': 'endDrag($event)',
    '(pointercancel)': 'endDrag($event)',
    '(keydown)': 'onKeyDown($event)',
    '(dblclick)': 'onDoubleClick($event)',
    '(click)': '$event.stopPropagation()',
  },
})
export class DeColumnResizerComponent {
  readonly label = input<string>('');
  readonly minWidth = input<number | undefined>(undefined);
  readonly maxWidth = input<number | undefined>(undefined);
  /** The width already stored for this column, if it has been resized before. */
  readonly currentWidth = input<number | undefined>(undefined);
  /** Measures the header cell, so a drag starts from the width on screen. */
  readonly measure = input.required<() => number>();

  readonly resized = output<number>();
  readonly committed = output<void>();
  readonly reset = output<void>();

  protected readonly MIN = MIN_COLUMN_WIDTH;

  /** How far one arrow-key press moves the edge. */
  private static readonly KEYBOARD_STEP = 16;

  private drag: { startX: number; startWidth: number } | null = null;

  /**
   * A stored width is authoritative; measuring is the fallback for a column
   * that has never been dragged. Measuring first would also let an unlaid-out
   * header report 0 and snap the column to its minimum.
   */
  private startWidth(): number {
    return this.currentWidth() ?? this.measure()();
  }

  protected onPointerDown(event: PointerEvent): void {
    // Only a primary-button drag resizes; let everything else through.
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    this.drag = { startX: event.clientX, startWidth: this.startWidth() };
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
  }

  protected onPointerMove(event: PointerEvent): void {
    if (!this.drag) return;
    this.resized.emit(this.drag.startWidth + (event.clientX - this.drag.startX));
  }

  protected endDrag(event: PointerEvent): void {
    if (!this.drag) return;
    this.drag = null;
    (event.target as HTMLElement).releasePointerCapture?.(event.pointerId);
    this.committed.emit();
  }

  protected onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      event.stopPropagation();
      const delta = event.key === 'ArrowLeft' ? -DeColumnResizerComponent.KEYBOARD_STEP : DeColumnResizerComponent.KEYBOARD_STEP;
      this.resized.emit(this.startWidth() + delta);
      this.committed.emit();
      return;
    }
    // Enter/Home restore the column, matching the double-click gesture.
    if (event.key === 'Enter' || event.key === 'Home') {
      event.preventDefault();
      event.stopPropagation();
      this.reset.emit();
    }
  }

  protected onDoubleClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.reset.emit();
  }
}
